import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect } from "react";
import { Bold, Italic, Heading1, Heading2, List, ListOrdered, Quote, Undo2, Redo2 } from "lucide-react";

/**
 * LOOXS rich text editor built on Tiptap.
 * Emits HTML through `onChange`. `value` is the initial HTML.
 */
export default function RichTextEditor({ value = "", onChange, placeholder = "Write the essay…" }) {
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: { levels: [1, 2, 3] } })],
    content: value,
    editorProps: {
      attributes: { class: "tiptap-editor outline-none", "data-testid": "journal-body-editor" },
    },
    onUpdate({ editor }) { onChange?.(editor.getHTML()); },
  });

  // Keep external `value` in sync when it changes from outside (e.g. after reset).
  useEffect(() => {
    if (editor && value !== editor.getHTML()) editor.commands.setContent(value || "");
    // eslint-disable-next-line
  }, [value]);

  if (!editor) return null;
  const Btn = ({ testid, active, onClick, children }) => (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`w-8 h-8 flex items-center justify-center border transition-colors ${active ? "bg-gold text-ink border-gold" : "border-ivory/20 text-ivory/70 hover:border-gold hover:text-gold"}`}
    >
      {children}
    </button>
  );
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1">
        <Btn testid="rt-h1"        active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}><Heading1 className="w-4 h-4" strokeWidth={1.25} /></Btn>
        <Btn testid="rt-h2"        active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="w-4 h-4" strokeWidth={1.25} /></Btn>
        <Btn testid="rt-bold"      active={editor.isActive("bold")}       onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="w-4 h-4" strokeWidth={1.5} /></Btn>
        <Btn testid="rt-italic"    active={editor.isActive("italic")}     onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="w-4 h-4" strokeWidth={1.5} /></Btn>
        <Btn testid="rt-bullet"    active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="w-4 h-4" strokeWidth={1.5} /></Btn>
        <Btn testid="rt-ordered"   active={editor.isActive("orderedList")}onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="w-4 h-4" strokeWidth={1.5} /></Btn>
        <Btn testid="rt-quote"     active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote className="w-4 h-4" strokeWidth={1.5} /></Btn>
        <span className="w-2" />
        <Btn testid="rt-undo"      onClick={() => editor.chain().focus().undo().run()}><Undo2 className="w-4 h-4" strokeWidth={1.5} /></Btn>
        <Btn testid="rt-redo"      onClick={() => editor.chain().focus().redo().run()}><Redo2 className="w-4 h-4" strokeWidth={1.5} /></Btn>
      </div>
      <EditorContent editor={editor} data-placeholder={placeholder} />
    </div>
  );
}
